import { memoize } from "lodash";

export type ChordProPartType =
  | "verse"
  | "chorus"
  | "bridge"
  | "image"
  | "carriage-return"
  | "comment"
  | "choruscomment"
  | "tab"
  | "x_url"
  | "x_video"
  | "x_audio"
  | "x_lyrics"
  | "x_pdf"
  | "x_audio_recorder";

export type ChordProPart = {
  type: ChordProPartType;
  linesParsed: ParsedLine[];
  url?: string; // x_url
  title?: string; // x_url
  tagName?: string; // x_video
  height?: string; // x_image
  width?: string; // x_image
  src?: string; // x_url
};

export type ParsedLine = ChordProBlock | ChordProBlock[];

export type ChordProBlockType = "chord" | "text" | "space";

export type ChordProBlock = {
  type: ChordProBlockType;
  text: string | null;
  originalChord?: string;
};

export type ChordProParsed = {
  parts: ChordProPart[];
  chords: string[];
  title?: string;
  subtitle?: string;
  composer?: string;
  artist?: string;
  key?: string;
  transpose?: number;
  capo?: number;
  tempo?: string;
  time?: string;
  duration?: string;
  x_chordposition?: string;
  x_instrument?: string;
  x_audio_recorder?: string;
};

const CHORD_RE = /\[(.*?)\]/;
const CHORD_OR_TEXT_RE = /(\[(.*?)\])|([^[\b\s]+)/g;

const LOG_TAG = "🪄 chordProParse 🪄";

const DEBUG = false;

let transposeTo = 0;
let linesIndex = 0; // the current line number being parsed
let lines: string[] = []; // the array of lines

export const parseChordProString = memoize(
  (text: string, argTransposeTo = 0): ChordProParsed => {
    //const t0 = performance.now();

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const debug = (...args: any) => {
      if (DEBUG) {
        console.debug(LOG_TAG, ...args);
      }
    };

    transposeTo = argTransposeTo;
    debug("parseChordProString()", { transposeTo, argTransposeTo });

    const chordProParsed: ChordProParsed = {
      parts: [],
      chords: [],
    };

    chordProParsed.chords = extractChords(text, transposeTo);

    linesIndex = 0;
    lines = text.split("\n");
    parseLines(chordProParsed);

    //const t1 = performance.now();
    //debug(`⏲️ parseChordProString on ${chordProParsed.title} took ${t1 - t0}ms`);
    return chordProParsed;
  },

  // Preserve all arguments in the memoized cache key
  (text: string, argTransposeTo?: number) => text + argTransposeTo,
);

function parseLines(chordProParsed: ChordProParsed) {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const debug = (...args: any) => {
    if (DEBUG) {
      console.debug(LOG_TAG, ...args);
    }
  };

  //debug("Initial lines", lines);
  while (linesIndex < lines.length) {
    const line = lines[linesIndex].trim();
    debug("parseChordProString() line iterator", {
      line,
      length: lines.length,
      linesIndex,
    });
    linesIndex++;
    if (isDirective(line)) {
      debug("parseChordProString() isDirective", { line, linesIndex });
      doDirective(line, chordProParsed);
    } else if (isComment(line)) {
      // do nothing for comments
      debug("parseChordProString() isComment", { line, linesIndex });
    } else if (isCarriageReturn(line)) {
      debug("parseChordProString() isCarriageReturn", {
        line,
        linesIndex,
      });
      // Don't want our first part to be a return
      if (chordProParsed.parts.length) {
        chordProParsed.parts.push({ type: "carriage-return", linesParsed: [] });
      }
    } else {
      debug("parseChordProString() else must be verse", {
        line,
        linesIndex,
      });
      // A line with no directive and it must NOT be within a directive block
      // like chorus or verse. We assume this is lyrics so we create a verse.
      linesIndex--; // include this line in the new verse
      doBlock("verse", [], chordProParsed);
    }
  }
}

function doBlock(
  type: ChordProPartType,
  closingDirectives: string[],
  chordProParsed: ChordProParsed,
) {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const debug = (...args: any) => {
    if (DEBUG) {
      console.debug(LOG_TAG, ...args);
    }
  };

  debug("doBlock() start", {
    type,
    linesIndex,
    closingDirectives,
  });

  // Convert to lowercase
  for (let i = 0; i < closingDirectives.length; i++) {
    closingDirectives[i] = closingDirectives[i].toLowerCase();
  }

  const block: ChordProPart = { type, linesParsed: [] };

  while (linesIndex < lines.length) {
    const line = lines[linesIndex];
    debug("doBlock() line iterator", { type, line, linesIndex });
    linesIndex++;
    if (matchesClosingDirectives(line, closingDirectives)) {
      // Yay! This specific block type has a matching closing directive!
      debug(`${type} block matches closingDirective by ${line}`);
      break;
    }
    if (isCarriageReturn(line) && closingDirectives.length === 0) {
      debug(
        `doBlock() blank line at index ${linesIndex} closes the current ${type} block`,
      );
      break;
    }
    if (isDirective(line)) {
      debug("doBlock() isDirective", { type, line, linesIndex });
      doDirective(line, chordProParsed);
      //throw new Error("do we ever hit this?");
    } else if (type === "tab") {
      debug("doBlock() parsing TAB line", { type, line, linesIndex });
      if (!line) break;
      block.linesParsed.push({
        type: "text",
        text: line,
      });
    } else if (line) {
      debug("doBlock() parsing line", { type, line, linesIndex });
      const chordsAndLyrics = parseLine(line, transposeTo) as ChordProBlock[];
      block.linesParsed.push(chordsAndLyrics);
    }
  }

  chordProParsed.parts.push(block);
}

// Break a line into its atomic, most granular parts.
// We split on spaces so that we can preserve distance between words, inline
// chords, and chords one after another, regardless of chordposition.
export function parseLine(line: string, transposeTo = 0): ParsedLine {
  const parsedLine: ChordProBlock[] = [];
  line.split(" ").forEach((chunk, i, chunks) => {
    const matches = chunk.match(CHORD_OR_TEXT_RE) || [];
    for (const chordOrText of matches) {
      const matchesIfChord = chordOrText.match(CHORD_RE);
      if (matchesIfChord) {
        const originalChord = matchesIfChord[1];
        let text = originalChord;
        if (transposeTo) {
          const baseChord = originalChord.slice(0, 1);
          const baseChordTransposed = transpose(baseChord, transposeTo);
          text = originalChord.replace(baseChord, baseChordTransposed);
        }
        parsedLine.push({
          type: "chord",
          originalChord,
          text,
        });
      } else {
        parsedLine.push({
          type: "text",
          text: chordOrText,
        });
      }
    }

    if (i < chunks.length - 1) {
      parsedLine.push({
        type: "space",
        text: " ",
      });
    }
  });
  return parsedLine;
}

// For list of directives see:
//   https://www.chordpro.org/chordpro/ChordPro-Directives.html
function doDirective(line: string, chordProParsed: ChordProParsed) {
  const aMatches = line.match(/{([^:}]+)(.*)}/);
  if (!aMatches) {
    console.warn(`Warning: Could not find directive in line: ${line}`);
    return;
  }

  let parameters = "";
  if (aMatches[2]) {
    // These are the parameters to the directive.
    parameters = aMatches[2].trim();
    if (":" === parameters.charAt(0)) {
      // trim leading ":"
      parameters = parameters.substring(1).trim();
    }
  }

  let directive = aMatches[1].toLowerCase().trim();
  // Translate song property aliases first.
  switch (directive) {
    case "t":
      directive = "title";
      break;
    case "c":
      directive = "comment";
      break;
    case "st":
      directive = "subtitle";
      break;
    // We accept the US spelling of "color" but export it as "colour".
    case "textcolor":
      directive = "textcolour";
      break;
    case "chordcolor":
      directive = "chordcolour";
      break;
  }

  switch (directive) {
    // song properties
    case "title":
    case "subtitle":
    case "composer":
    case "artist":
    case "key":
    case "tempo":
    case "duration":
    case "time":
    case "x_chordposition":
    case "x_instrument": // guitar, ukulele, uke, bass, mandolin
      //debug("Setting directive", directive, "to", parameters);
      chordProParsed[directive] = parameters;
      break;

    case "transpose":
    case "capo":
      chordProParsed[directive] = Number.parseInt(parameters, 10);
      break;

    // one-line but possibly recurring directives
    case "chorus":
      // Technically the "chorus" directive just displays a comment that says "Chorus".
      chordProParsed.parts.push({
        type: "choruscomment",
        linesParsed: [{ type: "text", text: "Chorus" }],
      });
      break;
    case "comment":
    case "x_audio":
    // {x_video: src="https://songbook.studio/song/-MGGFO8Ofxti9IUbtUUb/my-default-heart"} }
    // <iframe width="100%" height="300"
    //   scrolling="no"
    //   frameborder="no"
    //   allow="autoplay"
    //   src="https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/1799728006&color=%23ff5500&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true"
    //   ></iframe><div style="font-size: 10px; color: #cccccc;line-break: anywhere;word-break: normal;overflow: hidden;white-space: nowrap;text-overflow: ellipsis; font-family: Interstate,Lucida Grande,Lucida Sans Unicode,Lucida Sans,Garuda,Verdana,Tahoma,sans-serif;font-weight: 100;"><a href="https://soundcloud.com/commoner" title="Commoner" target="_blank" style="color: #cccccc; text-decoration: none;">Commoner</a> · <a href="https://soundcloud.com/commoner/my-default-heart" title="My Default Heart"
    //   target="_blank"
    //   style="color: #cccccc; text-decoration: none;">My Default Heart</a></div>
    case "x_video":
    case "image":
    case "x_pdf":
    case "x_url": {
      // Extract each parameter and set it as a property on the "part".
      const hPart: ChordProPart = parseParameters(parameters);

      // map `src` to `url` for internal consistency.
      if (hPart.src && !hPart.url) {
        hPart.url = hPart.src;
      }

      // Set a tagName param so we know which implementation to use in SongView
      if (
        directive === "x_audio" &&
        hPart.url &&
        hPart.url.indexOf("spotify.com") !== -1
      ) {
        hPart.tagName = "iframe";
      }

      if (hPart.url) {
        // Dropbox fixes
        hPart.url = fixDropboxUrl(hPart.url);

        // Spotify open -> embed link fixes
        if (hPart.url.indexOf("https://open.spotify.com/") === 0) {
          // Extract the URI and convert "/" to ":".
          const uri = hPart.url
            .substring("https://open.spotify.com/".length)
            .replace(/\//g, ":");
          if (uri) {
            hPart.url = `https://embed.spotify.com/?uri=spotify:${uri}`;
          }
        }

        // Youtube
        const youTubeUrl = getYoutubeUrl(hPart.url);
        if (youTubeUrl) {
          hPart.url = youTubeUrl;
        }
      }
      hPart.type = directive;
      // TODO FIX
      // @ts-ignore
      hPart.linesParsed = [{ text: parameters }];
      chordProParsed.parts.push(hPart);
      break;
    }

    // blocks of lyrics
    case "start_of_chorus":
    case "soc":
      doBlock("chorus", ["end_of_chorus", "eoc"], chordProParsed);
      break;
    case "start_of_verse":
      doBlock("verse", ["end_of_verse"], chordProParsed);
      break;
    case "start_of_tab":
    case "sot":
      doBlock("tab", ["end_of_tab", "eot"], chordProParsed);
      break;
    case "end_of_chorus":
    case "eoc":
    case "end_of_verse":
    case "end_of_tab":
    case "eot":
      console.error(
        `ERROR: Should never reach this closing directive: ${directive}`,
        chordProParsed.title,
      );
      break;

    // These case clauses are all useless - just listing them here
    // for future implementation.
    // case "ns":
    // case "new_song":
    // case "lyricist":
    // case "copyright":
    // case "album":
    // case "year":
    // case "meta":
    // case "comment_italic":
    // case "ci":
    // case "comment_box":
    // case "cb":

    // // no plan to support these
    // // eslint-disable-next-line no-fallthrough
    // case "start_of_grid":
    // case "end_of_grid":
    // case "new_page":
    // case "np":
    // case "new_physical_page":
    // case "npp":
    // case "column_break":
    // case "grid":
    // case "g":
    // case "no_grid":
    // case "ng":
    // case "titles":
    // case "columns":
    // case "col":

    // // Custom directives
    // // eslint-disable-next-line no-fallthrough
    // case "tabfont":
    // case "tabsize":
    // case "tabcolour":
    case "x_audio_recorder": {
      const hPart: ChordProPart = parseParameters(parameters);
      hPart.type = directive;
      hPart.linesParsed = [{ type: "text", text: parameters }];
      chordProParsed.parts.push(hPart);
      break;
    }
    default:
      console.warn(
        `Warning: No handler for directive ${directive}`,
        chordProParsed.title,
      );
  }
}

///////////////////////////////////////////////////

function matchesClosingDirectives(line: string, closingDirectives: string[]) {
  if (isDirective(line) && closingDirectives.length) {
    for (let i = 0; i < closingDirectives.length; i++) {
      if (0 === line.toLowerCase().trim().indexOf(`{${closingDirectives[i]}`)) {
        return true;
      }
    }
  }

  return false;
}

function isDirective(line: string) {
  return "{" === line.charAt(0) && "}" === line.charAt(line.length - 1);
}

function isComment(line: string) {
  return "#" === line.charAt(0);
}

function isCarriageReturn(line: string) {
  return line === "";
}

function getYoutubeUrl(url: string) {
  let aMatches: RegExpMatchArray | null;
  let youtubeId = "";
  // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
  if ((aMatches = url.match(/https:\/\/www.youtube.com\/watch\?v=([^&]*)/))) {
    youtubeId = aMatches[1];
    // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
  } else if ((aMatches = url.match(/https:\/\/youtu.be\/([^&]*)/))) {
    youtubeId = aMatches[1];
  }

  if (youtubeId) {
    return `https://www.youtube.com/embed/${youtubeId}`;
  }

  return "";
}

// The Dropbox Share URL for images and audio files end with "?dl=0" which takes you to an HTML
// page containing the object. You have to replace that with "?raw=1" to get the actual file.
function fixDropboxUrl(url: string) {
  if (
    -1 !== url.indexOf("https://www.dropbox.com/") &&
    -1 !== url.indexOf("?dl=0")
  ) {
    return url.replace("?dl=0", "?raw=1");
  }
  return url;
}

// Return a hash based on name=value tuples.
// example: src="https://example.com/score.png" width="100" height="80" title="Bob and Mary"
function parseParameters(sParams: string): ChordProPart {
  const hParams: ChordProPart = {
    type: "verse",
    linesParsed: [],
  };

  // Extract one tuple at a time.
  while (sParams) {
    // biome-ignore lint/style/noParameterAssign: <explanation>
    sParams = sParams.trim();
    let aMatches = sParams.match(/([^=]*)=\"(.*?)\"(.*)/);
    if (!aMatches) {
      aMatches = sParams.match(/([^=]*)=\'(.*?)\'(.*)/);
      if (!aMatches) {
        aMatches = sParams.match(/([^=]*)=([^ ]*)(.*)/);
        if (!aMatches) {
          break;
        }
        //else { console.log("aMatches 3:", aMatches); }
      }
      //else { console.log("aMatches 2:", aMatches); }
    }
    //else { console.log("aMatches 1:", aMatches); }

    const sKey = aMatches[1].toLowerCase() as keyof ChordProPart;
    const sVal = cleanQuotes(aMatches[2]) as
      | ChordProPartType
      | ParsedLine[]
      | string;
    // @ts-ignore
    hParams[sKey] = sVal;
    // biome-ignore lint/style/noParameterAssign: <explanation>
    sParams = aMatches[3];
  }

  return hParams;
}

// Remove leading and trailing quotes from a string.
function cleanQuotes(s: string) {
  // biome-ignore lint/style/noParameterAssign: <explanation>
  s = s.trim();

  const c0 = s.charAt(0);
  if ("'" === c0 || '"' === c0) {
    // Assume that if it _starts_ with a quote then it ends with a quote and that
    // quote character is not used within the string.
    // biome-ignore lint/style/noParameterAssign: <explanation>
    s = s.replace(c0, "").replace(c0, "").trim();
  }

  return s;
}

// https://stackoverflow.com/questions/7936843/how-do-i-transpose-music-chords-using-javascript
export function transpose(chord: string, amount: number) {
  if (amount === 0) return chord;
  const scale = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  const normalizeMap: Record<string, string> = {
    Cb: "B",
    Db: "C#",
    Eb: "D#",
    Fb: "E",
    Gb: "F#",
    Ab: "G#",
    Bb: "A#",
    "E#": "F",
    "B#": "C",
  };
  return chord.replace(/[CDEFGAB](b|#)?/g, (match) => {
    const i =
      (scale.indexOf(normalizeMap[match] ? normalizeMap[match] : match) +
        amount) %
      scale.length;
    return scale[i < 0 ? i + scale.length : i];
  });
}

export function durationToSeconds(duration: string): number {
  const matches = duration.match(/([0-9]*):([0-9]*)/);
  if (!matches) {
    return Number.parseInt(duration, 10);
  }
  const mins = Number.parseInt(matches[1], 10);
  const secs = Number.parseInt(matches[2], 10);
  return 60 * mins + secs;
}

export function extractChords(text: string, transposeTo = 0): string[] {
  const chords: string[] = [];
  const matches = text.match(/\[([a-gA-G][^/|]*?)\]/g);
  if (matches) {
    for (const match of Array.from(new Set(matches))) {
      const chord = transpose(match.replace(/[\[\]]/g, ""), transposeTo);
      chords.push(chord);
    }
  }
  return chords;
}
